import {
  createNotification,
  listNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  getStoredTokens,
  useNotificationStore,
} from "@slumbr/core";
import type { SlumbrNotification } from "@slumbr/core";
import { useEffect } from "react";

// Shared across all hook instances so multiple consumers (shell layout +
// notification panel) don't stack refetches on the same visibility flip.
const REFETCH_DEBOUNCE_MS = 60_000;
let lastNotificationFetchAt = 0;

export function useSlumbrNotifications() {
  const notifications = useNotificationStore((s) => s.notifications);
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const addNotification = useNotificationStore((s) => s.addNotification);
  const setNotifications = useNotificationStore((s) => s.setNotifications);
  const markAsReadLocal = useNotificationStore((s) => s.markAsRead);
  const markAllAsReadLocal = useNotificationStore((s) => s.markAllAsRead);

  useEffect(() => {
    if (!getStoredTokens()) {
      return;
    }

    let cancelled = false;

    const fetchNotifications = () => {
      lastNotificationFetchAt = Date.now();
      void listNotifications()
        .then((items) => {
          if (!cancelled) {
            setNotifications(items);
          }
        })
        .catch((error) => {
          console.error("Failed to load notifications:", error);
        });
    };

    fetchNotifications();

    // Refetch when the tab becomes visible again so a tab hidden for an
    // hour catches up on notifications missed while realtime was paused.
    // Debounced to at most once per 60s across all hook instances.
    if (typeof document === "undefined") {
      return () => {
        cancelled = true;
      };
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastNotificationFetchAt < REFETCH_DEBOUNCE_MS) return;
      if (!getStoredTokens()) return;
      fetchNotifications();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [setNotifications]);

  const sendNotification = async (
    notification: Omit<SlumbrNotification, "id" | "createdAt" | "read">
  ) => {
    const created = await createNotification(notification);
    addNotification(created);
  };

  const markAsRead = async (notificationId: string) => {
    markAsReadLocal(notificationId);
    try {
      await markNotificationAsRead(notificationId);
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    markAllAsReadLocal();
    try {
      await markAllNotificationsAsRead();
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  };

  return {
    notifications,
    unreadCount,
    sendNotification,
    markAsRead,
    markAllAsRead,
  };
}
